import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, ChevronDown, ChevronUp, Send, CheckCircle,
  Edit2, Loader2, Copy, Mail, X, Eye, Image as ImageIcon,
  Trash2, CheckSquare, Square, Paperclip, RefreshCw, Archive,
} from 'lucide-react';
import { formatDateTimeShort } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function buildEmailText(deliveries, locationName) {
  if (!deliveries || deliveries.length === 0) return '';

  const blocks = deliveries.map(({ delivery, supplier, diffItems }) => {
    const wbs = delivery?.wbs_number || '';
    const date = delivery?.delivery_date || '';
    const kdNr = supplier?.customer_number || '';

    const artikelBlocks = diffItems.map(item => {
      const menge = Math.abs(item.difference_amount ?? 1) || 1;
      return [
        item.article_name,
        `  • Lekkerland-Art-Nr.: ${item.supplier_art_nr || ''}`,
        `  • Interne Art-Nr.: ${item.fremd_article_number || ''}`,
        `  • Fehlende Menge: ${menge} Stück`,
      ].join('\n');
    }).join('\n\n');

    return [
      `Rechnungsnummer: ${wbs}`,
      `Kundennummer: ${kdNr}`,
      `Lieferdatum: ${date}`,
      ``,
      artikelBlocks,
    ].join('\n');
  });

  const intro = deliveries.length > 1
    ? `bei den folgenden ${deliveries.length} Lieferungen haben folgende Artikel gefehlt bzw. wurden beschädigt geliefert:`
    : `bei der heutigen Lieferung haben folgende Artikel gefehlt bzw. wurden beschädigt geliefert:`;

  const separator = deliveries.length > 1 ? '\n\n' + '─'.repeat(40) + '\n\n' : '\n\n';

  return [
    `Hallo Lekkerland-Team,`,
    ``,
    intro,
    ``,
    blocks.join(separator),
    ``,
    `Die Ware wurde nicht geliefert, jedoch vollständig in Rechnung gestellt.`,
    ``,
    `Bitte erstellen Sie uns hierfür eine Gutschrift.`,
    ``,
    `Vielen Dank und freundliche Grüße,`,
    locationName || '',
  ].join('\n');
}

function buildSubject(deliveries, locationName, supplier, isNachreklamation = false) {
  const kdNr = supplier?.customer_number || '';
  const wbsList = deliveries.map(d => d.delivery?.wbs_number || '').filter(Boolean).join(', ');
  const prefix = isNachreklamation ? 'NACHREKLAMATION' : 'Reklamation';
  return `Kd-Nr. ${kdNr} / ${prefix} RE-Nr. ${wbsList} - ${locationName || ''}`;
}

// ─── Client-seitige Bildkomprimierung ────────────────────────────────────────
async function compressImage(file, maxWidth = 1200, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          blob => {
            if (!blob) return reject(new Error('Canvas Blob fehlgeschlagen'));
            const reader2 = new FileReader();
            reader2.onload = ev => resolve({
              base64: ev.target.result.split(',')[1],
              dataUrl: ev.target.result,
              filename: file.name.replace(/\.[^.]+$/, '.jpg'),
              contentType: 'image/jpeg',
              sizeKb: Math.round(blob.size / 1024),
            });
            reader2.readAsDataURL(blob);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Bild-Upload-Bereich ─────────────────────────────────────────────────────
const ImageUploadArea = ({ images, onAdd, onRemove }) => {
  const inputRef = useRef(null);
  const [compressing, setCompressing] = useState(false);

  const handleFiles = async (files) => {
    const remaining = 5 - images.length;
    if (remaining <= 0) {
      toast.warning('Maximal 5 Bilder erlaubt');
      return;
    }
    const toProcess = Array.from(files).slice(0, remaining);
    setCompressing(true);
    try {
      const compressed = await Promise.all(toProcess.map(f => compressImage(f)));
      onAdd(compressed);
    } catch {
      toast.error('Bild konnte nicht komprimiert werden');
    } finally {
      setCompressing(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="image-upload-area">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
          <Paperclip className="h-3.5 w-3.5" />
          Foto-Anhänge (max. 5)
        </p>
        {images.length < 5 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={compressing}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors disabled:opacity-50"
            data-testid="add-image-btn"
          >
            {compressing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
            Bild hinzufügen
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
        data-testid="image-file-input"
      />
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group" data-testid={`image-preview-${idx}`}>
              <img
                src={img.dataUrl}
                alt={img.filename}
                className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
              />
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-0.5 truncate w-16">
                {img.sizeKb}KB
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── E-Mail-Vorschau-Modal ────────────────────────────────────────────────────
const EmailPreviewModal = ({
  initialTo, subject: initialSubject, body: initialBody,
  images, onImagesChange, onClose, onSent,
}) => {
  const [to, setTo] = useState(initialTo || '');
  const [subject, setSubject] = useState(initialSubject || '');
  const [body, setBody] = useState(initialBody || '');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editingBody, setEditingBody] = useState(false);

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Bitte E-Mail-Adresse des Empfängers eingeben');
      return;
    }
    setSending(true);
    try {
      await onSent({ to: to.trim(), subject, body, images });
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    const full = `An: ${to}\nBetreff: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(full);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = full;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Text in Zwischenablage kopiert');
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="email-preview-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            <h3 className="text-base font-bold text-gray-900 dark:text-white">E-Mail Vorschau & Versand</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-testid="preview-close">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>

        {/* Scrollbarer Inhalt */}
        <div className="flex-1 overflow-y-auto">
          {/* Meta-Felder */}
          <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-gray-500 dark:text-gray-400 w-14 flex-shrink-0">An:</span>
              <input
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="empfaenger@lieferant.de"
                className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="recipient-email-input"
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-gray-500 dark:text-gray-400 w-14 flex-shrink-0">Betreff:</span>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="flex-1 text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                data-testid="subject-input"
              />
            </div>
          </div>

          {/* E-Mail-Body */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Nachricht</p>
              <button
                onClick={() => setEditingBody(v => !v)}
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid="toggle-edit-body"
              >
                <Edit2 className="h-3 w-3" />
                {editingBody ? 'Vorschau' : 'Bearbeiten'}
              </button>
            </div>
            {editingBody ? (
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={12}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                data-testid="body-textarea"
              />
            ) : (
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans leading-relaxed bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3 max-h-60 overflow-y-auto" data-testid="preview-body">
                {body}
              </pre>
            )}
          </div>

          {/* Bildanhänge */}
          <div className="px-5 pb-4">
            <ImageUploadArea
              images={images}
              onAdd={newImgs => onImagesChange(prev => [...prev, ...newImgs].slice(0, 5))}
              onRemove={idx => onImagesChange(prev => prev.filter((_, i) => i !== idx))}
            />
          </div>
        </div>

        {/* Aktionen */}
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 space-y-2 flex-shrink-0 bg-white dark:bg-gray-900">
          <button
            onClick={handleSend}
            disabled={sending || !to.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
            data-testid="send-brevo-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Wird gesendet …' : 'Jetzt verbindlich senden (Brevo)'}
          </button>
          <button
            onClick={handleCopy}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-colors border ${
              copied
                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
            data-testid="copy-text-btn"
          >
            <Copy className="h-3.5 w-3.5" />
            {copied ? 'Kopiert!' : 'Text in Zwischenablage kopieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Differenz-Artikel-Zeile ──────────────────────────────────────────────────
const DiffRow = ({ item, onQuantityChange }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.difference_amount ?? 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/lieferung/reklamation/items/${item.id}`,
        { difference_amount: n }, { headers: authHeader() });
      onQuantityChange(item.id, n);
      toast.success('Fehlmenge aktualisiert');
      setEditing(false);
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700" data-testid="diff-row">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.article_name}</p>
        <p className="text-xs text-gray-400 font-mono">Art-Nr: {item.supplier_art_nr || item.fremd_article_number || '-'}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {editing ? (
          <>
            <input
              type="number" min="0" value={val}
              onChange={e => setVal(e.target.value)}
              className="w-16 text-center text-sm border border-primary rounded-lg px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-600"
              autoFocus data-testid="edit-diff-input"
            />
            <button onClick={save} disabled={saving}
              className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
              data-testid="save-diff-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
            </button>
          </>
        ) : (
          <>
            <span className="text-sm font-bold text-red-500 dark:text-red-400 min-w-[2.5rem] text-right">
              -{item.difference_amount ?? 0}
            </span>
            <button onClick={() => setEditing(true)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-primary transition-colors"
              title="Menge korrigieren" data-testid="edit-diff-btn">
              <Edit2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Reklamations-Karte pro Lieferung ────────────────────────────────────────
const ReklamationCard = ({
  delivery: initDelivery, supplier, diffItems: initDiff, locationName,
  isSelected, onToggleSelect, onSingleSend, onSent, onArchive, isArchived = false,
}) => {
  const [open, setOpen] = useState(true);
  const [diffItems, setDiffItems] = useState(initDiff);
  const delivery = initDelivery;
  const alreadySent = !!delivery.reklamation_sent_at;

  const handleQuantityChange = (itemId, newQty) => {
    setDiffItems(prev => prev.map(i => i.id === itemId ? { ...i, difference_amount: newQty } : i));
  };

  return (
    <div
      className={`bg-white dark:bg-gray-900 border rounded-xl overflow-hidden transition-all ${
        isSelected
          ? 'border-primary ring-2 ring-primary/20'
          : alreadySent
            ? 'border-green-200 dark:border-green-800/40'
            : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid="reklamation-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4">
        {/* Checkbox – immer sichtbar */}
        <button
          onClick={() => onToggleSelect(delivery.id)}
          className="flex-shrink-0 text-gray-400 hover:text-primary transition-colors"
          data-testid={`select-delivery-${delivery.id}`}
        >
          {isSelected
            ? <CheckSquare className="h-5 w-5 text-primary" />
            : <Square className="h-5 w-5" />
          }
        </button>

        <button
          onClick={() => setOpen(v => !v)}
          className="flex-1 flex items-center gap-3 text-left min-w-0"
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            alreadySent ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
          }`}>
            {alreadySent
              ? <CheckCircle className="h-5 w-5 text-green-500" />
              : <AlertTriangle className="h-5 w-5 text-red-500" />
            }
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {supplier?.name || 'Unbekannter Lieferant'} – WBS {delivery.wbs_number || ''}
            </p>
            <p className="text-xs text-gray-400">
              {delivery.delivery_date || ''} · {diffItems.length} Fehlmenge(n)
              {alreadySent && (
                <span className="ml-2 text-green-600 dark:text-green-400 font-medium">
                  · Zuletzt gesendet: {formatDateTimeShort(delivery.reklamation_sent_at)}
                </span>
              )}
            </p>
          </div>
          {open ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                 : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          }
        </button>
      </div>

      {/* Detail-Panel – immer erreichbar */}
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-800 p-4 space-y-4">
          {/* "Bereits gesendet"-Hinweis */}
          {alreadySent && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-300">
              <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                Für diese Lieferung wurde bereits eine Reklamation gesendet. Du kannst unten
                eine <strong>Nachreklamation</strong> für neu entdeckte Fehlmengen abschicken.
              </span>
            </div>
          )}

          {diffItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-red-500 dark:text-red-400">
                Gemeldete Fehlmengen
              </p>
              {diffItems.map(item => (
                <DiffRow key={item.id} item={item} onQuantityChange={handleQuantityChange} />
              ))}
            </div>
          )}

          {!supplier?.complaint_email && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              Keine Reklamations-E-Mail beim Lieferanten hinterlegt. Bitte im Lieferanten-Profil ergänzen.
            </div>
          )}

          <button
            onClick={() => onSingleSend({ delivery, supplier, diffItems, locationName })}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors ${
              alreadySent
                ? 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700'
                : 'bg-primary hover:bg-primary/90'
            }`}
            data-testid={`send-reklamation-btn-${delivery.id}`}
          >
            {alreadySent ? <RefreshCw className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {alreadySent ? 'Nachreklamation senden' : 'Reklamation senden'}
          </button>

          {/* Erledigt & Archivieren – nur nach Versand */}
          {alreadySent && !isArchived && onArchive && (
            <button
              onClick={() => onArchive(delivery.id)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-green-400 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              data-testid={`archive-reklamation-btn-${delivery.id}`}
            >
              <Archive className="h-4 w-4" />
              Erledigt &amp; archivieren
            </button>
          )}
          {isArchived && (
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-500 py-1">
              <Archive className="h-3.5 w-3.5" />
              Archiviert · wird nach 60 Tagen gelöscht
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Hauptseite ───────────────────────────────────────────────────────────────
export default function Reklamation() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [images, setImages] = useState([]);
  const [previewConfig, setPreviewConfig] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archivedDeliveries, setArchivedDeliveries] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lieferung/reklamation`, { headers: authHeader() });
      setDeliveries(r.data || []);
    } catch { toast.error('Fehler beim Laden der Reklamationen'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleSelect = (deliveryId) => {    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(deliveryId) ? next.delete(deliveryId) : next.add(deliveryId);
      return next;
    });
  };

  const handleArchive = async (deliveryId) => {
    try {
      await axios.patch(`${API}/lieferung/reklamation/${deliveryId}/archive`, {}, { headers: authHeader() });
      toast.success('Erledigt! Wird nach 60 Tagen automatisch gelöscht.');
      setDeliveries(prev => prev.filter(d => d.id !== deliveryId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Archivieren');
    }
  };

  const fetchArchived = async () => {
    setArchiveLoading(true);
    try {
      const r = await axios.get(`${API}/lieferung/reklamation/archived`, { headers: authHeader() });
      setArchivedDeliveries(r.data || []);
    } catch { toast.error('Archiv konnte nicht geladen werden'); }
    finally { setArchiveLoading(false); }
  };

  // Einzel-Versand (ein Button pro Karte)
  const handleSingleSend = ({ delivery, supplier, diffItems, locationName }) => {
    const isNachreklamation = !!delivery.reklamation_sent_at;
    const deliveriesForPreview = [{ delivery, supplier, diffItems, locationName }];
    const body = buildEmailText(deliveriesForPreview, locationName);
    const subject = buildSubject(deliveriesForPreview, locationName, supplier, isNachreklamation);
    setPreviewConfig({
      deliveriesPayload: deliveriesForPreview,
      to: supplier?.complaint_email || '',
      subject,
      body,
    });
    setImages([]);
    setShowPreview(true);
  };

  // Kombinierter Versand (Multi-WBS)
  const handleCombinedSend = () => {
    const selected = deliveries.filter(d => selectedIds.has(d.id));
    if (selected.length === 0) return;

    // Lieferanten-Check: Alle sollten denselben Lieferanten haben (Warnung wenn nicht)
    const supplierIds = [...new Set(selected.map(d => d.supplier?.id).filter(Boolean))];
    if (supplierIds.length > 1) {
      toast.warning('Achtung: Ausgewählte Lieferungen haben unterschiedliche Lieferanten!');
    }

    const isNachreklamation = selected.some(d => !!d.reklamation_sent_at);
    const supplier = selected[0]?.supplier;
    const locationName = selected[0]?.location_name || '';
    const deliveriesForPreview = selected.map(d => ({
      delivery: d,
      supplier: d.supplier,
      diffItems: d.diff_items || [],
      locationName: d.location_name || '',
    }));
    const body = buildEmailText(deliveriesForPreview, locationName);
    const subject = buildSubject(deliveriesForPreview, locationName, supplier, isNachreklamation);
    setPreviewConfig({
      deliveriesPayload: deliveriesForPreview,
      to: supplier?.complaint_email || '',
      subject,
      body,
    });
    setImages([]);
    setShowPreview(true);
  };

  // Tatsächlicher Versand via Brevo
  const handleSend = async ({ to, subject, body, images: imgs }) => {
    if (!previewConfig) return;

    const deliveryIds = previewConfig.deliveriesPayload.map(d => d.delivery.id);
    const payload = {
      delivery_ids: deliveryIds,
      recipient_email: to,
      subject,
      body_text: body,
      images: imgs.map(img => ({
        filename: img.filename,
        content: img.base64,
        content_type: img.contentType,
      })),
    };

    try {
      await axios.post(`${API}/lieferung/reklamation/send-brevo`, payload, { headers: authHeader() });
      toast.success('Reklamation erfolgreich gesendet!');
      setShowPreview(false);
      setSelectedIds(new Set());
      setImages([]);
      setPreviewConfig(null);
      // Geendete Lieferungen als gesendet markieren
      setDeliveries(prev => prev.map(d =>
        deliveryIds.includes(d.id) ? { ...d, reklamation_sent_at: new Date().toISOString() } : d
      ));
    } catch (err) {
      const msg = err?.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen';
      toast.error(msg);
      throw err; // Damit der Modal-Button nicht schließt
    }
  };

  const openDeliveries = deliveries.filter(d => !d.reklamation_sent_at);
  const sentDeliveries = deliveries.filter(d => !!d.reklamation_sent_at);
  const selectedCount = deliveries.filter(d => selectedIds.has(d.id)).length;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-red-500" />
            Reklamationen
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Vom Mitarbeiter gemeldete Fehlmengen – prüfen, korrigieren, per E-Mail senden
          </p>
        </div>

        {/* Floating Multi-Select Bar */}
        {selectedCount > 1 && (
          <div
            className="sticky top-4 z-30 flex items-center justify-between gap-3 px-4 py-3 bg-primary text-white rounded-xl shadow-lg"
            data-testid="multi-select-bar"
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              <CheckSquare className="h-4 w-4" />
              {selectedCount} Lieferungen ausgewählt
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-white/80 hover:text-white underline"
                data-testid="clear-selection-btn"
              >
                Auswahl aufheben
              </button>
              <button
                onClick={handleCombinedSend}
                className="flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 bg-white text-primary rounded-lg hover:bg-white/90 transition-colors"
                data-testid="combined-send-btn"
              >
                <Mail className="h-4 w-4" />
                Zusammen senden
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : openDeliveries.length === 0 && sentDeliveries.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <div className="w-16 h-16 rounded-2xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-base font-semibold text-gray-700 dark:text-gray-300">Keine offenen Reklamationen</p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Sobald Mitarbeiter Fehlmengen melden, erscheinen sie hier.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {openDeliveries.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Offen</span>
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                    {openDeliveries.length > 9 ? '9+' : openDeliveries.length}
                  </span>
                  {openDeliveries.length > 1 && (
                    <button
                      onClick={() => {
                        const allOpenIds = new Set(openDeliveries.map(d => d.id));
                        const allSelected = openDeliveries.every(d => selectedIds.has(d.id));
                        if (allSelected) {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            openDeliveries.forEach(d => next.delete(d.id));
                            return next;
                          });
                        } else {
                          setSelectedIds(prev => new Set([...prev, ...allOpenIds]));
                        }
                      }}
                      className="ml-auto text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                      data-testid="select-all-btn"
                    >
                      {openDeliveries.every(d => selectedIds.has(d.id)) ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  )}
                </div>
                {openDeliveries.map(d => (
                  <ReklamationCard
                    key={d.id}
                    delivery={d}
                    supplier={d.supplier}
                    diffItems={d.diff_items || []}
                    locationName={d.location_name || ''}
                    isSelected={selectedIds.has(d.id)}
                    onToggleSelect={handleToggleSelect}
                    onSingleSend={handleSingleSend}
                    onSent={() => {}}
                  />
                ))}
              </div>
            )}

            {sentDeliveries.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-400 dark:text-gray-500">Gesendet</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                    – Nachreklamation jederzeit möglich
                  </span>
                </div>
                {sentDeliveries.map(d => (
                  <ReklamationCard
                    key={d.id}
                    delivery={d}
                    supplier={d.supplier}
                    diffItems={d.diff_items || []}
                    locationName={d.location_name || ''}
                    isSelected={selectedIds.has(d.id)}
                    onToggleSelect={handleToggleSelect}
                    onSingleSend={handleSingleSend}
                    onSent={() => {}}
                    onArchive={handleArchive}
                  />
                ))}

                {/* Archiv-Toggle */}
                <button
                  onClick={() => {
                    if (!showArchive) fetchArchived();
                    setShowArchive(v => !v);
                  }}
                  className="w-full mt-2 flex items-center justify-center gap-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 py-2 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl transition-colors"
                  data-testid="toggle-archive-btn"
                >
                  <Archive className="h-3.5 w-3.5" />
                  {showArchive ? 'Archiv ausblenden' : `Archiv anzeigen (erledigte Reklamationen)`}
                </button>

                {showArchive && (
                  <div className="space-y-2 pt-1">
                    {archiveLoading ? (
                      <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
                    ) : archivedDeliveries.length === 0 ? (
                      <p className="text-xs text-center text-gray-400 py-4">Noch keine archivierten Reklamationen</p>
                    ) : archivedDeliveries.map(d => (
                      <ReklamationCard
                        key={d.id}
                        delivery={d}
                        supplier={d.supplier}
                        diffItems={d.diff_items || []}
                        locationName={d.location_name || ''}
                        isSelected={false}
                        onToggleSelect={() => {}}
                        onSingleSend={handleSingleSend}
                        onSent={() => {}}
                        isArchived
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* E-Mail-Vorschau-Modal */}
      {showPreview && previewConfig && (
        <EmailPreviewModal
          initialTo={previewConfig.to}
          subject={previewConfig.subject}
          body={previewConfig.body}
          images={images}
          onImagesChange={setImages}
          onClose={() => { setShowPreview(false); setImages([]); }}
          onSent={handleSend}
        />
      )}
    </DashboardLayout>
  );
}
